import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, Validators, FormGroup } from '@angular/forms';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { Select } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { AutoComplete } from 'primeng/autocomplete';
import { AncillaryService } from '@services/ancillary.service';
import { AccountingReportService } from '@services/accounting-report-service.service';
import { FareType } from '@shared/interfaces/ancillary.interface';
import { Airlines, Providers } from '@shared/interfaces/accounting-report.interface';

export type AncillaryDialogMode = 'add' | 'edit';
export type AncillaryConfigMode = 'BUNDLE_CONTENT' | 'PROVIDER_SERVICE_MAPPING';

export interface AncillaryFormModel {
    id: number | null;
    mode: AncillaryConfigMode;
    ancillaryKey?: string;
    providerCode: string | null;
    airlineCode: string | null;
    fareTypeName: string | null;
    categoryName: string | null;
    amount: number | null;
    unit: string | null;
    mandatorySelection: boolean;
    priorityOrder: number | null;
    status: 'ACTIVE' | 'FORCED' | 'PASSIVE' | '';
    titlesByLanguage: { [k: string]: string };
}

@Component({
    selector: 'app-ancillary-create-edit',
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, InputTextModule, ButtonModule, CheckboxModule, Select, InputNumberModule, AutoComplete],
    templateUrl: './ancillary-create-edit.component.html'
})
export class AncillaryCreateEditComponent implements OnInit, OnChanges {
    @Input() mode: AncillaryDialogMode = 'add';
    @Input() data: Partial<AncillaryFormModel> | null = null;

    @Output() cancel = new EventEmitter<void>();
    @Output() save = new EventEmitter<AncillaryFormModel>();

    ancillaryService = inject(AncillaryService);
    accountingReportService = inject(AccountingReportService);
    fb = inject(FormBuilder);

    providers: Providers[] = [];
    airlines: Airlines[] = [];
    fareTypes: FareType[] = [];
    filteredFareTypeNames: string[] = [];
    categories: string[] = [];
    submitted = false;

    configModes = [
        { label: 'Bundle Content', value: 'BUNDLE_CONTENT' },
        { label: 'Provider Service Mapping', value: 'PROVIDER_SERVICE_MAPPING' }
    ];

    statusOptions = ['ACTIVE', 'FORCED', 'PASSIVE'];
    unitOptions = ['KG', 'PIECE'];

    formGroup: FormGroup = this.fb.group({
        id: [null],
        mode: ['BUNDLE_CONTENT', Validators.required],
        ancillaryKey: ['', [Validators.maxLength(255)]],
        providerCode: [null, Validators.required],
        airlineCode: [null],
        fareTypeName: [null],
        categoryName: [null, Validators.required],
        amount: [null, [Validators.min(0)]],
        unit: ['KG'],
        mandatorySelection: [false],
        priorityOrder: [null, [Validators.min(1), Validators.max(100)]],
        status: ['FORCED', Validators.required],
        titlesByLanguage: this.fb.group({
            TR: ['', [Validators.maxLength(255)]],
            NL: ['', [Validators.maxLength(255)]],
            DE: ['', [Validators.maxLength(255)]],
            EN: ['', [Validators.maxLength(255)]]
        })
    });

    ngOnInit(): void {
        this.loadLookups();
        this.formGroup.get('mode')?.valueChanges.subscribe((value) => {
            this.applyModeRules(value);
            this.applyTitleRules();
        });
        this.formGroup.get('categoryName')?.valueChanges.subscribe(() => this.applyTitleRules());
        this.formGroup.get('amount')?.valueChanges.subscribe(() => this.applyTitleRules());
        this.formGroup.get('providerCode')?.valueChanges.subscribe((providerCode) => {
            this.formGroup.patchValue({ fareTypeName: null }, { emitEvent: false });
            this.loadFareTypes(providerCode);
        });
        this.applyModeRules(this.formGroup.get('mode')?.value);
        this.applyTitleRules();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['data']) return;
        if (this.data) {
            this.formGroup.patchValue({
                id: this.data.id ?? null,
                mode: this.data.mode ?? 'PROVIDER_SERVICE_MAPPING',
                ancillaryKey: this.data.ancillaryKey ?? '',
                providerCode: this.data.providerCode ?? null,
                airlineCode: this.data.airlineCode ?? null,
                fareTypeName: this.data.fareTypeName ?? null,
                categoryName: this.data.categoryName ?? null,
                amount: this.data.amount ?? null,
                unit: this.data.unit ?? 'KG',
                mandatorySelection: this.data.mandatorySelection ?? false,
                priorityOrder: this.data.priorityOrder ?? null,
                status: this.data.status ?? 'ACTIVE',
                titlesByLanguage: {
                    TR: this.data.titlesByLanguage?.['TR'] ?? '',
                    NL: this.data.titlesByLanguage?.['NL'] ?? '',
                    DE: this.data.titlesByLanguage?.['DE'] ?? '',
                    EN: this.data.titlesByLanguage?.['EN'] ?? ''
                }
            });
            this.loadFareTypes(this.data.providerCode ?? null, false);
        } else {
            this.formGroup.reset({
                mode: 'BUNDLE_CONTENT',
                unit: 'KG',
                mandatorySelection: false,
                status: 'FORCED',
                titlesByLanguage: { TR: '', NL: '', DE: '', EN: '' }
            });
            this.fareTypes = [];
        }
        this.applyModeRules(this.formGroup.get('mode')?.value);
        this.applyTitleRules();
        this.submitted = false;
    }

    loadLookups(): void {
        this.accountingReportService.getProviders().subscribe({
            next: (res) => (this.providers = res || []),
            error: () => (this.providers = [])
        });
        this.accountingReportService.getAirlines().subscribe({
            next: (res) => (this.airlines = res || []),
            error: () => (this.airlines = [])
        });
        this.ancillaryService.getCategories().subscribe({
            next: (res) => (this.categories = (res || []).map((c: any) => c?.name).filter((x: any) => !!x)),
            error: () => (this.categories = [])
        });
    }

    onCancel(): void {
        this.cancel.emit();
    }

    onSave(): void {
        this.submitted = true;
        if (this.formGroup.invalid) {
            this.formGroup.markAllAsTouched();
            return;
        }
        const v = this.formGroup.getRawValue() as any;
        this.save.emit({
            id: v.id,
            mode: v.mode,
            ancillaryKey: v.mode === 'PROVIDER_SERVICE_MAPPING' ? v.ancillaryKey : undefined,
            providerCode: v.providerCode,
            airlineCode: v.airlineCode,
            fareTypeName: v.fareTypeName,
            categoryName: v.categoryName,
            amount: v.amount,
            unit: v.amount !== null && v.amount !== undefined ? v.unit : null,
            mandatorySelection: !!v.mandatorySelection,
            priorityOrder: v.priorityOrder,
            status: v.status,
            titlesByLanguage: {
                TR: v.titlesByLanguage?.TR ?? '',
                NL: v.titlesByLanguage?.NL ?? '',
                DE: v.titlesByLanguage?.DE ?? '',
                EN: v.titlesByLanguage?.EN ?? ''
            }
        });
    }

    searchFareTypes(event: { query: string }): void {
        const query = (event.query || '').toLowerCase();
        this.filteredFareTypeNames = this.fareTypes
            .map((fare) => fare.name)
            .filter((name) => !query || name.toLowerCase().includes(query));
    }

    get isBundleContent(): boolean {
        return this.formGroup.get('mode')?.value === 'BUNDLE_CONTENT';
    }

    get isZeroBaggageBundleContent(): boolean {
        const amount = this.formGroup.get('amount')?.value;
        return this.isBundleContent && amount !== null && amount !== undefined && Number(amount) <= 0 && this.isBaggageCategory(this.formGroup.get('categoryName')?.value);
    }

    private loadFareTypes(providerCode: string | null, preserveCurrent = true): void {
        const provider = this.providers.find((item) => item.code === providerCode);
        if (!provider?.id) {
            this.fareTypes = [];
            return;
        }
        this.ancillaryService.getFareTypes(provider.id).subscribe({
            next: (res) => {
                this.fareTypes = res || [];
            },
            error: () => (this.fareTypes = [])
        });
    }

    private applyModeRules(mode: AncillaryConfigMode): void {
        const key = this.formGroup.get('ancillaryKey');
        const airline = this.formGroup.get('airlineCode');
        const fare = this.formGroup.get('fareTypeName');
        if (mode === 'PROVIDER_SERVICE_MAPPING') {
            key?.setValidators([Validators.required, Validators.maxLength(255)]);
            fare?.setValidators([Validators.required]);
            airline?.clearValidators();
            if (!this.formGroup.get('status')?.value) {
                this.formGroup.patchValue({ status: 'ACTIVE' }, { emitEvent: false });
            }
        } else {
            key?.setValidators([Validators.maxLength(255)]);
            key?.setValue('', { emitEvent: false });
            fare?.clearValidators();
            airline?.setValidators([Validators.required]);
            if (!this.formGroup.get('status')?.value || this.formGroup.get('status')?.value === 'ACTIVE') {
                this.formGroup.patchValue({ status: 'FORCED' }, { emitEvent: false });
            }
        }
        key?.updateValueAndValidity({ emitEvent: false });
        fare?.updateValueAndValidity({ emitEvent: false });
        airline?.updateValueAndValidity({ emitEvent: false });
    }

    private applyTitleRules(): void {
        const titleEn = this.formGroup.get('titlesByLanguage.EN');
        const validators = this.isZeroBaggageBundleContent ? [Validators.maxLength(255)] : [Validators.required, Validators.maxLength(255)];
        titleEn?.setValidators(validators);
        titleEn?.updateValueAndValidity({ emitEvent: false });
    }

    private isBaggageCategory(categoryName: string | null): boolean {
        return ['BAGGAGE', 'CHECKED_BAG', 'CABIN_BAG'].includes((categoryName || '').toUpperCase());
    }
}
